'use client';

import React, { useMemo } from 'react';

import { z } from 'zod';

import Button from '@/components/V2/Button';
import PasswordInput from '@/components/V2/PasswordInput/PasswordInput';
import PasswordStrengthBar from '@/components/V2/PasswordStrengthBar/PasswordStrengthBar';
import { useForgotPasswordContext } from '@/contexts/ForgotPasswordContext';
import useForm from '@/helpers/hooks/useForm';

import Spinner from '../PetProfile/Spinner';

const resetPasswordSchema = z
  .object({
    password: z.string().min(1, 'Password is required'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
type ResetPasswordFormErrors = Partial<Record<keyof ResetPasswordFormData, string>>;

const ResetPasswordForm = () => {
  const { handleResetPasswordSubmit, isLoading } = useForgotPasswordContext();
  const initialState = useMemo(
    () => ({
      password: '',
      confirmPassword: '',
    }),
    []
  );

  const {
    data,
    errors,
    handleChange,
    onSubmit: handleFormSubmit,
  } = useForm<ResetPasswordFormData, ResetPasswordFormErrors>({
    initialState,
    errors: {} as ResetPasswordFormErrors,
    validationSchema: resetPasswordSchema as any,
  });

  const handleSubmit = () => {
    handleFormSubmit({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>, () => {
      if (data.password === data.confirmPassword) {
        handleResetPasswordSubmit(data.password);
      }
    });
  };

  return (
    <div className="">
      <h2 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-3xl v2-text-[#101828] v2-mb-8">New Password</h2>

      <div className="v2-flex v2-flex-col">
        <PasswordInput
          name="password"
          label="Password*"
          value={data.password}
          onChange={handleChange}
          error={errors.password}
          className="v2-mb-5"
          inputClassName="!v2-py-[9px]"
        />
        <PasswordInput
          name="confirmPassword"
          label="Confirm Password*"
          value={data.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
          className="v2-mb-5"
          inputClassName="!v2-py-[9px]"
        />
      </div>

      <div className="v2-mb-6">
        <PasswordStrengthBar password={data.password} />
      </div>

      <div className="v2-mb-6">
        <Button
          type="button"
          scheme="primary"
          variant="filled"
          btnSize="lg"
          className="v2-w-full v2-h-[44px] v2-flex v2-justify-center v2-items-center v2-font-sweet-sans-pro !v2-font-bold v2-text-base"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading && <Spinner height={20} className="v2-mr-2" />}
          Reset Password
        </Button>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
